/**
 * smSplineStyle — user view-preference for the SHAPE of transition splines in
 * State Machine graphs (ENG-1061).
 *
 * The default `step` splines (getSmoothStepPath, 90° rounded corners) overlap
 * badly on dense state machines and it becomes impossible to tell which line
 * connects which states. This lets the user switch to:
 *   - `arched`   — curved bezier splines (getBezierPath)
 *   - `straight` — direct straight lines (getStraightPath)
 *
 * This is a per-user VIEW preference, not blueprint data: it changes only how
 * the graph is drawn, never what it means or what gets saved to the server. So
 * it lives in localStorage (like other editor view prefs), carries no schema
 * version, and is never written into a blueprint row. TransitionEdge.jsx reads
 * the chosen style off `edge.data.splineStyle` and picks the matching ReactFlow
 * path function.
 */
const KEY = 'aos-sm-spline-style'
const CURVATURE_KEY = 'aos-sm-spline-curvature'

// Arched (curved bezier) is the default: the old right-angle `step` splines
// overlapped into an unreadable tangle on dense machines, which is the whole
// reason ENG-1061 exists. A user's saved choice always wins over this.
export const DEFAULT_SPLINE_STYLE = 'arched'

// Ordered for the toolbar segmented control.
export const SPLINE_STYLES = [
  { value: 'step',     label: 'Step',     title: 'Right-angle stepped splines' },
  { value: 'arched',   label: 'Arched',   title: 'Curved bezier splines (default)' },
  { value: 'straight', label: 'Straight', title: 'Direct straight lines' },
]

const VALID = new Set(SPLINE_STYLES.map(s => s.value))

export function isValidSplineStyle(v) {
  return VALID.has(v)
}

/** Current SM spline style; falls back to the default for missing/invalid values. */
export function getSmSplineStyle() {
  try {
    const v = globalThis.localStorage?.getItem(KEY)
    return isValidSplineStyle(v) ? v : DEFAULT_SPLINE_STYLE
  } catch {
    return DEFAULT_SPLINE_STYLE
  }
}

/**
 * Persist the SM spline style. Ignores invalid values (returns the effective
 * style either way) so a bad caller can never wedge the editor into a broken
 * shape.
 */
export function setSmSplineStyle(v) {
  if (!isValidSplineStyle(v)) return getSmSplineStyle()
  try {
    globalThis.localStorage?.setItem(KEY, v)
  } catch {
    // Non-fatal: a private-mode / disabled localStorage just means the choice
    // doesn't survive a reload — the in-session React state still applies it.
  }
  return v
}

// ── Arched-spline bend tightness (ENG-1061 follow-up) ────────────────────────
// How pronounced the curve is on ARCHED transitions — a single view preference
// driving both arched code paths in TransitionEdge:
//   - plain edges: the control-point offset in bowedBezierPath (edgeWaypoints).
//     ReactFlow's own getBezierPath ignores curvature on forward edges, so we
//     use our own bezier that scales the bend on every edge; the default (0.25)
//     reproduces getBezierPath exactly.
//   - rerouted edges: the Catmull-Rom control-point offset in smoothSplinePath,
//     via splineTensionForCurvature() below.
// Only meaningful while the `arched` style is active; `step`/`straight` ignore
// it. Like the style itself, this is a per-user view pref (localStorage, no
// schema version, never written to a blueprint row).
// Real curvature units (what bowedBezierPath consumes). The slider presents
// these on a normalized 0..1 scale — see the conversion helpers below.
export const SPLINE_CURVATURE_MIN = 0.25       // gentlest usable bend
export const SPLINE_CURVATURE_MAX = 0.55       // most pronounced
export const DEFAULT_SPLINE_CURVATURE = 0.50
export const SPLINE_CURVATURE_STEP = 0.01      // normalized-slider step (0..1)

/** Clamp any input to the valid curvature range; NaN/garbage → the default. */
export function clampSplineCurvature(v) {
  const n = typeof v === 'number' ? v : parseFloat(v)
  if (!Number.isFinite(n)) return DEFAULT_SPLINE_CURVATURE
  return Math.min(SPLINE_CURVATURE_MAX, Math.max(SPLINE_CURVATURE_MIN, n))
}

// ── Normalized (0..1) slider scale ───────────────────────────────────────────
// The stored/applied value is always in real curvature units; the slider works
// in 0..1 so its position reads independently of the underlying range.

/** Real curvature → normalized 0..1 (0 = MIN, 1 = MAX). */
export function curvatureToNormalized(c) {
  const span = SPLINE_CURVATURE_MAX - SPLINE_CURVATURE_MIN
  if (span <= 0) return 0
  return (clampSplineCurvature(c) - SPLINE_CURVATURE_MIN) / span
}

/** Normalized 0..1 → real curvature, clamped to the valid range. */
export function normalizedToCurvature(n) {
  const t = Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : curvatureToNormalized(DEFAULT_SPLINE_CURVATURE)
  return clampSplineCurvature(SPLINE_CURVATURE_MIN + t * (SPLINE_CURVATURE_MAX - SPLINE_CURVATURE_MIN))
}

/** Current arched-spline curvature; missing/invalid → the default. */
export function getSmSplineCurvature() {
  try {
    const raw = globalThis.localStorage?.getItem(CURVATURE_KEY)
    if (raw == null) return DEFAULT_SPLINE_CURVATURE
    return clampSplineCurvature(raw)
  } catch {
    return DEFAULT_SPLINE_CURVATURE
  }
}

/** Persist the arched-spline curvature (clamped). Returns the effective value. */
export function setSmSplineCurvature(v) {
  const c = clampSplineCurvature(v)
  try {
    globalThis.localStorage?.setItem(CURVATURE_KEY, String(c))
  } catch {
    // Non-fatal — same rationale as setSmSplineStyle above.
  }
  return c
}

/**
 * Map the single curvature knob to the Catmull-Rom tension smoothSplinePath
 * wants (its control-point offset is `(p2 - p0) * tension`). The factor `2/3`
 * makes curvature 0.25 land on the historical `1/6` divisor (0.25·2/3 === 1/6),
 * so the two arched code paths — plain beziers and rerouted splines — bend
 * consistently across the shared curvature range.
 */
export function splineTensionForCurvature(curvature) {
  return clampSplineCurvature(curvature) * (2 / 3)
}
